import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchAccountsCacheKey,
  FetchFinAccountsFilters,
  FetchAccountsSort,
  FetchAccountsPage,
  FetchAccountsLimit,
  FetchAccountsGqlQuery,
  StatementPeriod
} from '../../accountsTypes';
import { FetchCacheAccountsQueryResponse } from '../../queries/fetchCacheAccounts.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_ACCOUNTS_FILTERS,
  INITIAL_ACCOUNTS_LIMIT,
  INITIAL_ACCOUNTS_PAGE,
  INITIAL_ACCOUNTS_SORT
} from '../../accountsConstants';

interface AccountsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface AccountsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface AccountsDefaultOptions {
  action?: string;
  cacheKey: FetchAccountsCacheKey;
  query: FetchAccountsGqlQuery;
  initialFilters?: FetchFinAccountsFilters;
  initialSort?: FetchAccountsSort;
  initialPage?: FetchAccountsPage;
  initialLimit?: FetchAccountsLimit;
  additionalParams?: { statementPeriods: StatementPeriod[] };
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type AccountsOptions = AccountsDefaultOptions &
  (AccountsWithPrefetchItemOptions | AccountsWithoutPrefetchItemOptions);

const scope = 'accounts';

function useCachePaginatedAccounts<
  FetchAccountsType = FetchCacheAccountsQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_ACCOUNTS_FILTERS,
  initialSort = INITIAL_ACCOUNTS_SORT,
  initialPage = INITIAL_ACCOUNTS_PAGE,
  initialLimit = INITIAL_ACCOUNTS_LIMIT,
  additionalParams,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: AccountsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchAccountsType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    additionalParams,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    accountsData: data,
    accounts: items,
    accountsError: itemsError,
    accountsErrorMessage: itemsErrorMessage,
    accountsTotalCount: itemsTotalCount,
    accountsFetched: isFetched,
    accountsLoading: isLoading,
    accountsIsPlaceholderData: isPlaceholderData,
    accountsFilters: currentFilters,
    accountsSort: currentSort,
    accountsPage: currentPage,
    accountsLimit: currentLimit,
    updateAccountCache: updateItemCache,
    updateAccountsCache: updateItemsCache,
    filterAccounts: filterItems,
    changeAccountsFilters: changeItemsFilters,
    clearAccountsFilters: clearItemsFilters,
    sortAccounts: sortItems,
    paginateAccounts: paginateItems,
    limitAccounts: limitItems,
    prefetchAccounts: prefetchItems,
    prefetchAccount: prefetchItem
  };
}

export default useCachePaginatedAccounts;
